import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link as RouterLink, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { ApiError, ConfirmDialog, LicenceNotice, RemoteContent, Title, formatClockTime, formatRange, formatUtcOffset, getClubTimezone, timezoneLabel, useAuth, useClubLocation, useSnackbar } from '@jupi/ui'
import { defectsLink, useAircraftStatuses } from '../features/calendar/aircraftStatus'
import { nightCheck, PERMISSIONS, SCHOOL_DEPARTMENTS, SCHOOL_PERMISSIONS, safeReturnPath, type BookingType } from '@jupi/shared'
import { LessonBookingSection, type LessonChoice } from '../components/LessonBookingSection'
import { BookingNeighbours } from '../components/BookingNeighbours'
import { BookingDetails } from '../components/BookingDetails'
import { StandbyOffer } from '../components/StandbyOffer'
import { TransferBookingDialog } from '../components/TransferBookingDialog'
import { useBookingAccess } from '../features/calendar/bookingAccess'
import { BOOKING_TYPE_ICONS, typeOf } from '../features/calendar/bookingType'
import { useEnrolments } from '../api/school'
import {
  useAircraft,
  useBookings,
  useCancelBooking,
  useCreateBooking,
  useMember,
  useTransferBooking,
  useUpdateBooking,
  type Booking,
} from '../api/queries'

interface FormState {
  registration: string
  // 'YYYY-MM-DDTHH:mm' in the browser's zone, which for this club is the
  // club's own: what a datetime-local input holds.
  startsAt: string
  endsAt: string
  estimatedFlightHours: string
  comment: string
}

// `inDialog`: the form over the page the member was on (desktop), rather
// than a page of its own. Same form, same Save and Cancel.
export default function BookingFormPage({ inDialog = false }: { inDialog?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // The calendar view the member came from, if any; Save and Cancel go back
  // to it rather than to a list.
  const returnTo = safeReturnPath((useLocation().state as { from?: unknown } | null)?.from)
  const { bookingId } = useParams()
  const [searchParams] = useSearchParams()
  const { showSnackbar } = useSnackbar()

  const { data: aircraft = [], isLoading: loadingAircraft } = useAircraft()
  const aircraftStatuses = useAircraftStatuses()
  const create = useCreateBooking()
  const update = useUpdateBooking()
  const cancel = useCancelBooking()
  const transfer = useTransferBooking()
  const [transferring, setTransferring] = useState(false)
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  const editing = Boolean(bookingId)
  // Editing loads the booking out of the cached list rather than adding a
  // GET /bookings/:id: the member arrived here from a list that already
  // holds it.
  const { data: allBookings = [], isLoading: loadingBooking } = useBookings({})
  const existing = useMemo<Booking | undefined>(
    () => allBookings.find((b) => b.bookingId === Number(bookingId)),
    [allBookings, bookingId],
  )

  const [form, setForm] = useState<FormState>(() => initialForm(searchParams))
  const { user, can } = useAuth()
  const { canOpen, canModify } = useBookingAccess()
  const { data: myProfile } = useMember(user?.id)
  // An instructor arriving from a student's training record books for the
  // student (?studentUserId=&enrolmentId=); everyone else for themselves.
  const studentUserId = existing?.userId ?? searchParams.get('studentUserId') ?? user?.id
  const [lesson, setLesson] = useState<LessonChoice>(() => {
    const enrolmentId = Number(searchParams.get('enrolmentId'))
    return Number.isInteger(enrolmentId) && enrolmentId > 0
      ? { enrolmentId, lessonId: null, instructorUserId: searchParams.get('studentUserId') ? (user?.id ?? null) : null }
      : { enrolmentId: null, lessonId: null, instructorUserId: null }
  })
  // Private unless a course is being booked for (the link from a student's
  // training record) or the member picks otherwise.
  const [bookingType, setBookingType] = useState<BookingType>(() =>
    Number(searchParams.get('enrolmentId')) > 0 ? 'training' : 'private',
  )
  const type: BookingType = existing ? typeOf(existing) : bookingType
  const { data: myEnrolments = [] } = useEnrolments({ studentUserId })
  const canBookTraining =
    myEnrolments.some((e) => e.status === 'active' && e.studentUserId === studentUserId) ||
    SCHOOL_DEPARTMENTS.some((d) => can(SCHOOL_PERMISSIONS[d].instruct))
  const bookingWindow = useMemo(() => {
    const startsAt = new Date(form.startsAt)
    const endsAt = new Date(form.endsAt)
    return Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt ? null : { startsAt, endsAt }
  }, [form.startsAt, form.endsAt])
  // Night is from the end of evening civil twilight to the start of morning
  // civil twilight (EASA). Computed here from the sun, with no web service.
  const place = useClubLocation()
  const night = useMemo(
    () => (bookingWindow ? nightCheck(bookingWindow.startsAt, bookingWindow.endsAt, place) : null),
    [bookingWindow, place],
  )
  // The times typed here are club-local, the zone of the clock in the header;
  // say so under each field, with the UTC equivalent pilots plan in.
  const timeNote = (at: Date | undefined) => {
    const zone = getClubTimezone()
    const label = t('booking.timeZoneNote', {
      zone: `(${formatUtcOffset(at ?? new Date(), zone)} ${timezoneLabel(zone)})`,
    })
    return at ? `${label} · ${t('booking.timeZoneUtc', { utc: formatClockTime(at, 'UTC') })}` : label
  }
  const [violations, setViolations] = useState<{ code: string; [key: string]: unknown }[]>([])
  const [conflicts, setConflicts] = useState<Booking[]>([])
  // How long a standby waits to be confirmed; '' is until the booking is over.
  const [dropHours, setDropHours] = useState<number | ''>('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  useEffect(() => {
    if (!existing) return
    setForm({
      registration: existing.registration,
      startsAt: isoLocal(new Date(existing.startsAt)),
      endsAt: isoLocal(new Date(existing.endsAt)),
      estimatedFlightHours: String(existing.estimatedFlightHours).replace('.', ','),
      comment: existing.comment ?? '',
    })
    setLesson({
      enrolmentId: existing.enrolmentId,
      lessonId: existing.lessonId,
      instructorUserId: existing.instructorUserId,
    })
  }, [existing])

  // The clash was for what was asked; ask for something else and it is gone,
  // and Save is back.
  useEffect(() => {
    setConflicts([])
  }, [form.registration, form.startsAt, form.endsAt])

  const set = (key: keyof FormState) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  // Moving the start carries the end with it, so the length the member has
  // already chosen survives; the end can still be set by itself afterwards.
  const setStart = (value: string) =>
    setForm((current) => {
      const before = new Date(current.startsAt).getTime()
      const after = new Date(value).getTime()
      const end = new Date(current.endsAt).getTime()
      if (Number.isNaN(before) || Number.isNaN(after) || Number.isNaN(end)) return { ...current, startsAt: value }
      return { ...current, startsAt: value, endsAt: isoLocal(new Date(end + (after - before))) }
    })

  const submit = async (event: FormEvent, kind: 'reservation' | 'option' = 'reservation') => {
    event.preventDefault()
    if (readOnly) return
    setViolations([])
    setConflicts([])
    setFieldErrors({})

    // Caught here so the message is ours and translated, not the schema's.
    if (!form.registration) {
      setFieldErrors({ registration: [t('booking.aircraftRequired')] })
      return
    }

    if (type === 'training' && !lesson.enrolmentId) {
      setViolations([{ code: 'training_needs_enrolment' }])
      return
    }

    const payload = {
      registration: form.registration,
      startDate: form.startsAt.slice(0, 10),
      startTime: form.startsAt.slice(11, 16),
      endDate: form.endsAt.slice(0, 10),
      endTime: form.endsAt.slice(11, 16),
      // Maintenance is the aircraft being worked on, not flown.
      estimatedFlightHours: type === 'maintenance' ? 0 : form.estimatedFlightHours,
      comment: form.comment || null,
      kind,
      bookingType: type,
      ...(kind === 'option' ? { dropHoursBeforeStart: dropHours === '' ? null : dropHours } : {}),
      ...(type === 'training'
        ? {
            userId: studentUserId,
            enrolmentId: lesson.enrolmentId,
            lessonId: lesson.lessonId,
            instructorUserId: kind === 'reservation' ? lesson.instructorUserId : null,
          }
        : {}),
    }

    try {
      if (editing && existing) {
        await update.mutateAsync({ ...payload, bookingId: existing.bookingId })
        showSnackbar(t('booking.updated'))
      } else {
        await create.mutateAsync(payload)
        showSnackbar(kind === 'option' ? t('booking.optionCreated') : t('booking.created'))
      }
      navigate(returnTo ?? '/bookings/mine', { replace: Boolean(returnTo) })
    } catch (err) {
      if (err instanceof ApiError) {
        setViolations(err.violations)
        setFieldErrors(err.fieldErrors)
        // 409 carries the bookings that clashed, so the member can see who
        // has it and decide whether to queue behind them.
        const clashing = (err.problem as unknown as { conflicts?: Booking[] } | null)?.conflicts
        if (clashing) setConflicts(clashing)
        if (err.violations.length === 0 && Object.keys(err.fieldErrors).length === 0) {
          showSnackbar(err.message, { severity: 'error' })
        }
      } else {
        showSnackbar(t('common.error'), { severity: 'error' })
      }
    }
  }

  const busy = create.isPending || update.isPending || cancel.isPending || transfer.isPending
  // Cancelling someone else's booking needs the same permission the API
  // checks, and a reason the holder is emailed.
  const cancellingForOther = existing !== undefined && existing.userId !== user?.id
  // What the member may do with this booking (the API decides the same): a
  // booking is opened by those it concerns and by administrators and
  // maintenance; changed or cancelled by its parties, and once it has ended
  // only by an administrator. Otherwise the form is a read-only view.
  const readOnly = existing !== undefined && !canModify(existing)
  const canCancel = existing !== undefined && canModify(existing)

  // Only a private reservation changes hands (the API says the same); its
  // holder does it, or someone who manages any booking.
  const canTransfer =
    existing !== undefined &&
    existing.kind === 'reservation' &&
    type === 'private' &&
    // A patrol booking is released, not handed over.
    !existing.isFirePatrol &&
    canModify(existing) &&
    ((existing.userId === user?.id && can(PERMISSIONS.bookingManageOwn)) || can(PERMISSIONS.bookingManageAny))

  const handleTransfer = async (target: { userId: string; displayName: string }) => {
    if (!existing) return
    try {
      await transfer.mutateAsync({ bookingId: existing.bookingId, toUserId: target.userId })
      setTransferring(false)
      showSnackbar(t('booking.transfer.done', { name: target.displayName }))
      navigate(returnTo ?? '/bookings/mine', { replace: Boolean(returnTo) })
    } catch (err) {
      const code = err instanceof ApiError ? err.violations[0]?.code : undefined
      showSnackbar(code ? t(`rules.${code}`) : err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  const handleCancel = async (reason?: string) => {
    if (!existing) return
    setConfirmingCancel(false)
    try {
      const result = await cancel.mutateAsync({ bookingId: existing.bookingId, reason })
      showSnackbar(t('booking.cancelledOk'))
      if (result.promoted.length > 0) {
        showSnackbar(t('calendar.waiting', { count: result.promoted.length }), { severity: 'info' })
      }
      if (returnTo) navigate(returnTo, { replace: true })
      else navigate(-1)
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }
  // A standby waits behind someone else's booking (the API refuses one behind
  // your own, or on a free slot).
  // The standby is for the member the booking is for (the student, when an
  // instructor books a lesson), and there is no such thing for maintenance.
  const canOfferOption = !editing && type !== 'maintenance' && conflicts.some((c) => c.userId !== studentUserId)

  // Someone else's booking is seen on the calendar, not opened.
  if (existing && !canOpen(existing)) return <Navigate to={returnTo ?? '/bookings/calendar'} replace />

  const heading = readOnly ? t('booking.title') : editing ? t('common.edit') : t('booking.new')
  const closeForm = () => (returnTo ? navigate(returnTo, { replace: true }) : navigate(-1))
  // In a dialog the dialog is the card.
  const Outer: React.ElementType = inDialog ? Box : Card
  const Inner: React.ElementType = inDialog ? Box : CardContent

  const content = (
    <>
      <RemoteContent isLoading={loadingAircraft || (editing && loadingBooking)} error={undefined}>
        <Outer>
          <Inner>
            <form onSubmit={(e) => submit(e)} noValidate>
              <Stack spacing={2}>
                {/* The member's own licence and medical, #225. */}
                {!existing && type !== 'maintenance' && user && studentUserId === user.id && myProfile && !myProfile.licenceNoticeOffAt ? (
                  <LicenceNotice
                    userId={user.id}
                    profileLink={
                      <Link component={RouterLink} to="/profile">
                        {t('licence.noticeProfile')}
                      </Link>
                    }
                  />
                ) : null}
                {/* A read-only view disables the fields, not the buttons below. */}
                <Stack spacing={2} component="fieldset" disabled={readOnly} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                {violations.length > 0 ? (
                  <Alert severity="warning">
                    <AlertTitle>{t('rules.refused')}</AlertTitle>
                    <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                      {violations.map((violation) => (
                        <li key={violation.code}>
                          {/* Each violation is rendered from its code in the
                              member's own language, with the server's numbers
                              interpolated -- never an echoed server sentence. */}
                          {t(`rules.${violation.code}`, formatViolation(violation))}
                        </li>
                      ))}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('rules.askCommittee')}
                    </Typography>
                  </Alert>
                ) : null}


                {existing ? <BookingDetails booking={existing} bookings={allBookings} /> : null}

                {editing ? (
                  // The type is part of what the booking is; a different one
                  // is a different booking.
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Chip
                      icon={<Icon icon={BOOKING_TYPE_ICONS[type]} />}
                      label={t(`booking.type.${type}`)}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t('booking.type.lockedHint')}
                    </Typography>
                  </Stack>
                ) : (
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    color="primary"
                    value={type}
                    aria-label={t('booking.type.label')}
                    onChange={(_, next: BookingType | null) => next && setBookingType(next)}
                  >
                    <ToggleButton value="private">
                      <Icon icon={BOOKING_TYPE_ICONS.private} width={18} style={{ marginRight: 6 }} />
                      {t('booking.type.private')}
                    </ToggleButton>
                    {canBookTraining ? (
                      <ToggleButton value="training">
                        <Icon icon={BOOKING_TYPE_ICONS.training} width={18} style={{ marginRight: 6 }} />
                        {t('booking.type.training')}
                      </ToggleButton>
                    ) : null}
                    {can(PERMISSIONS.maintenanceWrite) ? (
                      <ToggleButton value="maintenance">
                        <Icon icon={BOOKING_TYPE_ICONS.maintenance} width={18} style={{ marginRight: 6 }} />
                        {t('booking.type.maintenance')}
                      </ToggleButton>
                    ) : null}
                  </ToggleButtonGroup>
                )}

                <TextField
                  select
                  required
                  label={t('booking.aircraft')}
                  value={form.registration}
                  // The aircraft of an existing booking cannot be changed --
                  // that is a different booking. Legacy agreed: ProcessChange2
                  // updated only the times and the comment.
                  disabled={editing}
                  onChange={(e) => set('registration')(e.target.value)}
                  error={Boolean(fieldErrors.registration)}
                  helperText={fieldErrors.registration?.join(' ')}
                >
                  {aircraft.map((plane) => (
                    <MenuItem key={plane.registration} value={String(plane.registration)} disabled={!plane.available}>
                      {plane.registration} — {plane.aircraftType}
                      {plane.available ? '' : ` (${t('aircraft.unavailable')})`}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Defects on the chosen aircraft. A restriction does not stop
                    the booking; AOG days do (the server refuses them). None of
                    it matters to maintenance: the aircraft is not being flown. */}
                {(() => {
                  const status = aircraftStatuses.get(form.registration)
                  if (type === 'maintenance' || !status || status.light === 'airworthy') return null
                  return (
                    <Alert
                      severity={status.light === 'aog' ? 'error' : 'warning'}
                      action={
                        <Button color="inherit" size="small" component={RouterLink} to={defectsLink(form.registration)}>
                          {t('calendar.openDefects')}
                        </Button>
                      }
                    >
                      {status.light === 'aog' && status.aog
                        ? t('aircraftDefect.aogFormNote', { from: status.aog.from })
                        : t('aircraftDefect.aogBookingNote', {
                            restrictions:
                              status.restrictions.map((r) => t(`aircraftDefect.restrictions.${r}`)).join(', ') ||
                              t('calendar.restricted'),
                          })}
                    </Alert>
                  )
                })()}

                {type !== 'maintenance' && night && (night.startsInNight || night.endsInNight) ? (
                  <Alert
                    severity={aircraftStatuses.get(form.registration)?.restrictions.includes('no_night') ? 'error' : 'warning'}
                    icon={<Icon icon="mdi:weather-night" />}
                  >
                    <AlertTitle>{t('booking.night.title')}</AlertTitle>
                    {night.startsInNight ? <div>{t('booking.night.starts')}</div> : null}
                    {night.endsInNight ? <div>{t('booking.night.ends')}</div> : null}
                    {aircraftStatuses.get(form.registration)?.restrictions.includes('no_night') ? (
                      <div>
                        <strong>{t('booking.night.aircraftRestricted', { registration: form.registration })}</strong>
                      </div>
                    ) : null}
                    <div>{t('booking.night.requirement')}</div>
                  </Alert>
                ) : null}

                <TextField
                  type="datetime-local"
                  required
                  label={t('booking.starts')}
                  value={form.startsAt}
                  helperText={timeNote(bookingWindow?.startsAt)}
                  onChange={(e) => setStart(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  type="datetime-local"
                  required
                  label={t('booking.ends')}
                  value={form.endsAt}
                  helperText={timeNote(bookingWindow?.endsAt)}
                  onChange={(e) => set('endsAt')(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: form.startsAt } }}
                />

                <BookingNeighbours
                  bookings={allBookings}
                  registration={form.registration}
                  window={bookingWindow}
                  excludeBookingId={existing?.bookingId}
                />

                {type === 'maintenance' ? null : (
                <TextField
                  required
                  label={t('booking.flightTime')}
                  value={form.estimatedFlightHours}
                  onChange={(e) => set('estimatedFlightHours')(e.target.value)}
                  helperText={fieldErrors.estimatedFlightHours?.join(' ') ?? t('booking.flightTimeHint')}
                  error={Boolean(fieldErrors.estimatedFlightHours)}
                  // a text keypad on a phone (it has the colon of 1:30), and the comma Finnish
                  // members type is accepted by the API.
                  slotProps={{ htmlInput: { inputMode: 'text' } }}
                />
                )}

                {type === 'training' ? (
                  <LessonBookingSection
                    studentUserId={studentUserId}
                    value={lesson}
                    onChange={setLesson}
                    window={bookingWindow}
                    aircraftType={aircraft.find((a) => a.registration === form.registration)?.aircraftType ?? null}
                    editing={editing}
                  />
                ) : null}

                <TextField
                  label={t('booking.comment')}
                  value={form.comment}
                  onChange={(e) => set('comment')(e.target.value)}
                  multiline
                  minRows={2}
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                />

                </Stack>

                {/* Under the notes, above the buttons: where the member is looking
                    when Save is refused. */}
                {canOfferOption ? (
                  <StandbyOffer
                    conflicts={conflicts}
                    bookings={allBookings}
                    registration={form.registration}
                    window={bookingWindow}
                    dropHours={dropHours}
                    onDropHours={setDropHours}
                  />
                ) : null}

                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<Icon icon="mdi:close" />}
                    onClick={closeForm}
                  >
                    {readOnly ? t('common.close') : t('common.cancel')}
                  </Button>
                  {editing && canCancel ? (
                    <Button color="error" startIcon={<Icon icon="mdi:delete" />} disabled={busy} onClick={() => setConfirmingCancel(true)}>
                      {t('booking.cancelTitle')}
                    </Button>
                  ) : null}
                  {editing && canTransfer ? (
                    <Button startIcon={<Icon icon="mdi:account-switch" />} disabled={busy} onClick={() => setTransferring(true)}>
                      {t('booking.transfer.action')}
                    </Button>
                  ) : null}
                  {/* The slot is taken: a standby is all that can be saved, so it
                      takes the place of Save rather than sitting beside it. */}
                  {canOfferOption ? (
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<Icon icon="mdi:timer-sand" />}
                      disabled={busy}
                      onClick={(e) => submit(e, 'option')}
                    >
                      {t('rules.makeOption')}
                    </Button>
                  ) : null}
                  {readOnly || canOfferOption ? null : (
                    <Button type="submit" variant="contained" startIcon={<Icon icon="mdi:content-save" />} disabled={busy}>
                      {t('common.save')}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </form>
          </Inner>
        </Outer>
      </RemoteContent>

      <TransferBookingDialog
        booking={transferring ? (existing ?? null) : null}
        busy={transfer.isPending}
        onConfirm={handleTransfer}
        onClose={() => setTransferring(false)}
      />

      <ConfirmDialog
        open={confirmingCancel}
        title={t('booking.cancelTitle')}
        message={
          existing
            ? t('booking.cancelMessage', {
                aircraft: existing.registration,
                when: formatRange(existing.startsAt, existing.endsAt),
              })
            : ''
        }
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.close')}
        reasonLabel={cancellingForOther ? t('booking.cancelReason') : undefined}
        reasonRequired={cancellingForOther}
        onConfirm={handleCancel}
        onClose={() => setConfirmingCancel(false)}
      />
    </>
  )

  if (inDialog) {
    return (
      <Dialog open onClose={closeForm} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>{heading}</DialogTitle>
        <DialogContent dividers>{content}</DialogContent>
      </Dialog>
    )
  }
  return (
    <Box>
      <Title label={heading} />
      {content}
    </Box>
  )
}

function initialForm(searchParams: URLSearchParams): FormState {
  // Arriving from a tap on an empty calendar slot: prefill what that tap
  // already told us.
  const startParam = searchParams.get('start')
  const start = startParam ? new Date(startParam) : defaultStart()
  // A slid selection carries its own end; a plain tap gets two hours.
  const endParam = searchParams.get('end')
  const parsedEnd = endParam ? new Date(endParam) : null
  const end =
    parsedEnd && parsedEnd.getTime() > start.getTime()
      ? parsedEnd
      : new Date(start.getTime() + 2 * 3600_000)
  return {
    registration: searchParams.get('registration') ?? '',
    startsAt: isoLocal(start),
    endsAt: isoLocal(end),
    estimatedFlightHours: '1,0',
    comment: '',
  }
}

function defaultStart(): Date {
  const start = new Date()
  start.setHours(start.getHours() + 1, 0, 0, 0)
  return start
}

const isoLocal = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
const pad = (n: number) => String(n).padStart(2, '0')

// Round the server's raw numbers before they reach a sentence: "your
// booking would be 2.0833333 days" helps nobody.
function formatViolation(violation: { [key: string]: unknown }): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(violation)) {
    out[key] = typeof value === 'number' ? Number(value.toFixed(2)).toString().replace('.', ',') : value
  }
  return out
}
